import React, { useEffect, useRef, useState } from 'react';
import { useEdgeAwarePanelOffset, useOutsideClickHandler } from '@utils/hooks';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { SearchInput } from '../HierarchicalMultiSelect/search';
import { Checkbox } from '../Checkbox';

export type MultiSelectItem = {
  label: string;
  value: string;
  startDecorator?: React.ReactNode;
  subcategories?: MultiSelectItem[];
  recipeCount?: number;
};

interface MultiSelectDropdownProps {
  name: string;
  options: MultiSelectItem[];
  className?: string;
  placeholder?: string;
  defaultValue?: string[];
  value?: string[];
  onChange?: (value: string[]) => void;
  wrapperClassName?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  itemsPerPage?: number;
  isNotAll?: boolean;
  onlyAll?: boolean;
  labelClassName?: string;
  isHaveSubCategory?: boolean;
  onMainCategorySelect?: (categoryId: string) => void;
  subcategoryOptions?: MultiSelectItem[];
  onSubCategoryChange?: (subcategoryIds: string[]) => void;
  selectedSubCategories?: string[];
  showCount?: boolean;
  onOpen?: () => void;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  name,
  options,
  className,
  placeholder = 'Select Items',
  defaultValue = [],
  value,
  wrapperClassName,
  onChange,
  searchable = false,
  searchPlaceholder = 'Search...',
  itemsPerPage = 20,
  isNotAll,
  onlyAll = false,
  labelClassName,
  isHaveSubCategory = false,
  onMainCategorySelect,
  subcategoryOptions = [],
  //selectedMainCategories = [],
  onSubCategoryChange,
  selectedSubCategories = [],
  showCount = false,
  onOpen
}) => {
  const { t } = useTranslation();
  const menuRef = useRef(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const firstLoadRef = useRef(true);

  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(
    null
  );

  const [selectedItems, setSelectedItems] = useState<string[]>(
    defaultValue ?? []
  );
  const [tempSelectedItems, setTempSelectedItems] = useState<string[]>(
    defaultValue ?? []
  );
  const [tempSelectedSubcategories, setTempSelectedSubcategories] = useState<
    string[]
  >(selectedSubCategories ?? []);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);

  const { panelRef, offsetX } = useEdgeAwarePanelOffset(isOpen, [
    expandedCategoryId
  ]);

  // APPLY defaultValue ONLY on first mount
  useEffect(() => {
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
      if (defaultValue && defaultValue.length > 0) {
        setSelectedItems(defaultValue);
        setTempSelectedItems(defaultValue);
      }
    }
  }, []);

  //  SYNC WITH CONTROLLED `value` FROM REDUX / PARENT
  useEffect(() => {
    if (Array.isArray(value)) {
      setSelectedItems(value);
      // Only update temp state if dropdown is closed
      if (!isOpen) {
        setTempSelectedItems(value);
      }
    }
  }, [value]);

  useEffect(() => {
    if (!isOpen && Array.isArray(selectedSubCategories)) {
      const same =
        selectedSubCategories.length === tempSelectedSubcategories.length &&
        selectedSubCategories.every(
          (v, i) => v === tempSelectedSubcategories[i]
        );

      if (!same) {
        setTempSelectedSubcategories(selectedSubCategories);
      }
    }
  }, [selectedSubCategories, isOpen, tempSelectedSubcategories]);

  // FILTER + PAGINATE OPTIONS
  const filteredOptions = searchable
    ? options.filter(
        option =>
          typeof option.label === 'string' &&
          option.label.toLowerCase().includes(searchTerm?.toLowerCase() || '')
      )
    : options;

  const paginatedOptions = searchable
    ? filteredOptions.slice(0, currentPage * itemsPerPage)
    : filteredOptions;

  const hasMoreItems =
    searchable && filteredOptions.length > paginatedOptions.length;

  const toggleDropdown = () => {
    if (!isOpen) {
      // When opening, initialize temp state with current selected state
      // Prefer controlled value over internal state
      setTempSelectedItems(value ?? selectedItems);
      setTempSelectedSubcategories(selectedSubCategories);
      if (searchable) {
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
      // notify parent that dropdown was opened
      onOpen?.();
    }
    setIsOpen(!isOpen);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const loadMoreItems = () => setCurrentPage(prev => prev + 1);

  // CHECKBOX HANDLING
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;

    if (value === '__all__') {
      const visibleValues = paginatedOptions.map(item => item.value);

      if (checked) {
        const newSelected = [
          ...new Set([...tempSelectedItems, ...visibleValues])
        ];
        setTempSelectedItems(newSelected);
      } else {
        setTempSelectedItems(prev =>
          prev.filter(item => !visibleValues.includes(item))
        );
      }
      return;
    }

    //  Determine if we're adding or removing
    const isAdding = !tempSelectedItems.includes(value);

    //  Update the selected items first
    const newTempSelected = isAdding
      ? [...tempSelectedItems, value]
      : tempSelectedItems.filter(item => item !== value);

    setTempSelectedItems(newTempSelected);

    if (isHaveSubCategory && onMainCategorySelect) {
      const isMainCategory = options.some(opt => opt.value === value);
      if (isMainCategory) {
        setExpandedCategoryId(value);
        onMainCategorySelect(value);
      }
    }
  };

  //  Handle subcategory checkbox change
  const handleSubcategoryCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value, checked } = e.target;

    const newTempSelectedSubcategories = checked
      ? [...tempSelectedSubcategories, value]
      : tempSelectedSubcategories.filter(item => item !== value);

    setTempSelectedSubcategories(newTempSelectedSubcategories);
  };

  const handleDone = () => {
    setSelectedItems(tempSelectedItems);
    if (onChange) {
      onChange(tempSelectedItems);
    }

    //  Call subcategory change handler separately
    if (isHaveSubCategory && onSubCategoryChange) {
      onSubCategoryChange(tempSelectedSubcategories);
    }

    setIsOpen(false);
    setSearchTerm('');
  };

  useOutsideClickHandler(menuRef, () => {
    setIsOpen(false);
    setSearchTerm('');
    setCurrentPage(1);
  });

  const totalCount = isOpen
    ? tempSelectedItems.length + tempSelectedSubcategories.length
    : (value?.length ?? selectedItems.length) + selectedSubCategories.length;

  return (
    <div
      className={`relative inline-block text-left ${wrapperClassName ?? ''}`}
      ref={menuRef}>
      <div>
        <Button
          onClick={toggleDropdown}
          type="button"
          btnStyle="link"
          className={`inline-flex justify-between w-full px-2 py-2 text-sm font-semibold text-[#505050] rounded-md hover:bg-gray-50 ${className ?? ''}`}>
          {`${placeholder}${totalCount ? ` (${totalCount})` : ''}`}

          <svg
            className="w-5 h-5 ml-2 -mr-1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor">
            <path
              fillRule="evenodd"
              d="M6.293 9.293a1 1 0 011.414 0L10 11.586l2.293-2.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </Button>
      </div>

      {isOpen && (
        <div
          ref={panelRef}
          style={
            offsetX ? { transform: `translateX(${offsetX}px)` } : undefined
          }
          className={`${
            isHaveSubCategory ? 'flex' : ''
          } absolute z-[200] mt-2 max-w-[calc(100vw-2rem)] bg-white rounded-md shadow-lg`}>
          <div className="min-w-[215px] w-[215px] max-w-full py-3 dropdown-height">
            {searchable && (
              <SearchInput
                value={searchTerm}
                onChange={handleSearchChange}
                onClear={clearSearch}
                placeholder={searchPlaceholder}
                isFromMultiDropdown={true}
                className="px-3"
              />
            )}

            <div className="grid grid-cols-1 gap-1 overflow-y-auto max-h-[200px] px-1">
              {!isNotAll && paginatedOptions.length > 0 && (
                <div className="flex items-center px-2 pb-2 mt-3">
                  <Checkbox
                    value="__all__"
                    checked={
                      paginatedOptions.length > 0 &&
                      paginatedOptions.every(option =>
                        tempSelectedItems.includes(option?.value)
                      )
                    }
                    onChange={handleCheckboxChange}
                    name={name}
                    className="px-2 mr-3"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  />
                  <span className="text-gray-900 text-sm capitalize ml-2">
                    {t('all')}
                  </span>
                </div>
              )}

              {!onlyAll && paginatedOptions.length > 0 ? (
                paginatedOptions.map(item => {
                  const isSubcategoryPanelOpen =
                    isHaveSubCategory &&
                    expandedCategoryId === item.value &&
                    item.recipeCount &&
                    item.recipeCount > 0;

                  return (
                    <div
                      key={item.value}
                      className="flex items-center px-2 pb-2 hover:bg-gray-50 cursor-pointer">
                      <Checkbox
                        value={item.value}
                        checked={tempSelectedItems.includes(item.value)}
                        onChange={handleCheckboxChange}
                        name={name}
                        className="px-2 mr-3"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      />

                      <span
                        onClick={e => {
                          e.stopPropagation();

                          if (expandedCategoryId === item.value) {
                            setExpandedCategoryId(null); // minimize
                          } else {
                            setExpandedCategoryId(item.value); // expand
                            onMainCategorySelect?.(item.value);
                          }
                        }}
                        className={`text-gray-900 text-sm capitalize ml-2 flex-1 ${labelClassName}`}>
                        {item?.label}
                      </span>

                      {isHaveSubCategory &&
                        item.recipeCount &&
                        item.recipeCount > 0 &&
                        (isSubcategoryPanelOpen ? (
                          <>
                            <span className="ml-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 text-gray-700 text-xs font-semibold">
                              {item.recipeCount}
                            </span>
                            <svg
                              onClick={e => {
                                e.stopPropagation();

                                if (expandedCategoryId === item.value) {
                                  setExpandedCategoryId(null); // minimize
                                } else {
                                  setExpandedCategoryId(item.value); // expand
                                  onMainCategorySelect?.(item.value);
                                }
                              }}
                              className="w-4 h-4 text-gray-400"
                              fill="currentColor"
                              viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </>
                        ) : (
                          <span className="ml-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 text-gray-700 text-xs font-semibold">
                            {item.recipeCount}
                          </span>
                        ))}

                      {!isHaveSubCategory &&
                        showCount &&
                        (item as any).count !== undefined &&
                        (item as any).count > 0 && (
                          <span className="ml-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 text-gray-700 text-xs font-semibold">
                            {(item as any).count}
                          </span>
                        )}
                    </div>
                  );
                })
              ) : !onlyAll ? (
                <div className="px-4 py-2 text-sm text-gray-500 text-center">
                  {t('noOptionsFound')}
                </div>
              ) : null}

              {hasMoreItems && !onlyAll && (
                <div className="px-2 pt-2 border-t border-gray-200 mt-2">
                  <button
                    onClick={loadMoreItems}
                    className="w-full px-3 py-2 text-sm text-[#4f6f3a] hover:bg-blue-50 rounded-md transition-colors duration-200">
                    {t('loadMore')} (
                    {filteredOptions.length - paginatedOptions.length}{' '}
                    {t('remaining')})
                  </button>
                </div>
              )}
            </div>

            {(paginatedOptions.length > 0 || onlyAll) && (
              <div className="w-full mt-2 flex px-3">
                <Button
                  onClick={handleDone}
                  btnStyle="primary"
                  className="w-full rounded-lg px-2 py-2 font-semibold text-sm">
                  {t('done')}
                </Button>
              </div>
            )}
          </div>

          {isHaveSubCategory && expandedCategoryId !== null && (
            <div className="min-w-[215px] w-[215px] py-3 border-l border-gray-200 overflow-y-auto max-h-[400px]">
              <div className="px-3 pb-2">
                <span className="text-xs font-semibold text-gray-600 uppercase">
                  {t('subcategories')}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-1">
                {subcategoryOptions.map(item => (
                  <div
                    key={item.value}
                    className="flex items-center px-2 pb-2 hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      value={item.value}
                      checked={tempSelectedSubcategories.includes(item.value)}
                      onChange={handleSubcategoryCheckboxChange}
                      name={`${name}-subcategory`}
                      className="px-2 mr-3"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    />
                    <span className="text-gray-900 text-sm capitalize ml-2">
                      {item.label}
                    </span>
                    <span className="ml-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 text-gray-700 text-xs font-semibold">
                      {item.recipeCount}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
